/** @jsxImportSource ../i18n */
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDown, Newspaper, RefreshCw, SearchX } from 'lucide-react';
import { fetchPortfolio, fetchWatchlist, fetchLatestNews } from '../store/slices/portfolioSlice';
import { useLanguage } from '../contexts/LanguageContext';
import { BrokerGate } from '../components/shell/Broker';
import { NewsItem, impactOf } from '../components/news/NewsItem';
import { cx } from '../theme/cx';
import { formatDateTime } from '../theme/format';
import { Badge, Banner, Button, Card, EmptyState, PageHeader, SearchInput, SegmentedControl, SkeletonList } from '../components/ui';

const IMPACT_FILTERS = [
  { value: 'all', label: 'Toutes' },
  { value: 'positive', label: 'Positives' },
  { value: 'negative', label: 'Négatives' },
  { value: 'neutral', label: 'Neutres' },
];

const DOMINANT_LABELS = { positive: 'Plutôt positif', negative: 'Plutôt négatif', neutral: 'Neutre' };

const plural = (n, one, many) => `${n} ${n > 1 ? many : one}`;

// Dominant impact for a group header badge
const dominantImpact = (items) => {
  const counts = { positive: 0, negative: 0, neutral: 0 };
  items.forEach(i => { if (counts[i.impact] !== undefined) counts[i.impact]++; });
  if (counts.positive > counts.negative) return 'positive';
  if (counts.negative > counts.positive) return 'negative';
  return 'neutral';
};

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cx(
      'inline-flex h-8 items-center rounded-full border px-3 text-xs font-semibold transition-colors duration-150',
      active ? 'border-gold-500/40 bg-gold-500/15 text-gold-200' : 'border-line bg-surface-1 text-fg-muted hover:border-line-strong hover:text-fg',
    )}
  >
    {children}
  </button>
);

const LatestNews = () => {
  const dispatch = useDispatch();
  const { positions, watchlist, watchlistLoading, saxoConnected, latestNews, latestNewsLoading } = useSelector((state) => state.portfolio);
  const { language } = useLanguage();

  const [searchTerm, setSearchTerm] = useState('');
  const [impactFilter, setImpactFilter] = useState('all');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const pollRef = useRef(null);

  const buildStockList = useCallback(() => {
    const portfolioStocks = positions.map(p => ({ symbol: p.symbol, description: p.description }));
    const watchlistStocks = watchlist.map(w => ({ symbol: w.symbol, description: w.description }));
    const seen = new Set();
    return [...portfolioStocks, ...watchlistStocks].filter(s => {
      if (seen.has(s.symbol)) return false;
      seen.add(s.symbol);
      return true;
    });
  }, [positions, watchlist]);

  useEffect(() => {
    if (saxoConnected) {
      dispatch(fetchPortfolio());
      dispatch(fetchWatchlist());
    }
  }, [dispatch, saxoConnected]);

  useEffect(() => {
    if (saxoConnected && (positions.length > 0 || watchlist.length > 0) && !latestNews && !latestNewsLoading) {
      const allStocks = buildStockList();
      if (allStocks.length > 0) {
        dispatch(fetchLatestNews({ stocks: allStocks, language }));
      }
    }
  }, [dispatch, saxoConnected, positions, watchlist, latestNews, latestNewsLoading, language, buildStockList]);

  const isGenerating = latestNews?.generating === true;
  useEffect(() => {
    if (isGenerating && !latestNewsLoading) {
      pollRef.current = setInterval(() => {
        const allStocks = buildStockList();
        if (allStocks.length > 0) {
          dispatch(fetchLatestNews({ stocks: allStocks, language }));
        }
      }, 5000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isGenerating, latestNewsLoading, dispatch, language, buildStockList]);

  const handleRefresh = async () => {
    let currentWatchlist = watchlist;
    if (watchlist.length === 0 && !watchlistLoading) {
      const result = await dispatch(fetchWatchlist());
      if (result.payload && Array.isArray(result.payload)) {
        currentWatchlist = result.payload;
      }
    }
    const portfolioStocks = positions.map(p => ({ symbol: p.symbol, description: p.description }));
    const watchlistStocks = currentWatchlist.map(w => ({ symbol: w.symbol, description: w.description }));
    const seen = new Set();
    const allStocks = [...portfolioStocks, ...watchlistStocks].filter(s => {
      if (seen.has(s.symbol)) return false;
      seen.add(s.symbol);
      return true;
    });
    if (allStocks.length > 0) {
      dispatch(fetchLatestNews({ stocks: allStocks, language, forceRefresh: true }));
    }
  };

  const allItems = useMemo(() => latestNews?.items || [], [latestNews]);

  const filteredItems = useMemo(() => allItems
    .filter(item => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        item.symbol?.toLowerCase().includes(term) ||
        item.headline?.toLowerCase().includes(term) ||
        item.summary?.toLowerCase().includes(term) ||
        item.company?.toLowerCase().includes(term)
      );
    })
    .filter(item => impactFilter === 'all' || item.impact === impactFilter), [allItems, searchTerm, impactFilter]);

  // Group filtered items by symbol, preserving insertion order
  const groupedBySymbol = useMemo(() => {
    const groups = {};
    for (const item of filteredItems) {
      const sym = item.symbol || 'Inconnu';
      if (!groups[sym]) {
        groups[sym] = { company: item.company || sym, items: [] };
      }
      groups[sym].items.push(item);
    }
    return groups;
  }, [filteredItems]);

  const symbolKeys = Object.keys(groupedBySymbol);
  const symbols = [...new Set(allItems.map(i => i.symbol))];
  const busy = latestNewsLoading || watchlistLoading || isGenerating;

  const toggleGroup = (sym) => {
    setCollapsedGroups(prev => ({ ...prev, [sym]: !prev[sym] }));
  };

  let content = null;
  if (!isGenerating && latestNewsLoading && allItems.length === 0) {
    content = (
      <Card role="status">
        <span className="sr-only">Initialisation de la recherche d'actualités…</span>
        <SkeletonList items={4} />
      </Card>
    );
  } else if (!isGenerating && allItems.length === 0) {
    content = <Card><EmptyState icon={Newspaper} title="Aucune actualité marquante" description="Aucune actualité significative n'a été trouvée pour vos valeurs." /></Card>;
  } else if (filteredItems.length === 0 && allItems.length > 0) {
    content = <Card><EmptyState icon={SearchX} title="Aucune actualité ne correspond au filtre" description="Modifiez la recherche ou le filtre d'impact." /></Card>;
  } else if (symbolKeys.length > 0) {
    content = (
      <div className="space-y-4">
        {symbolKeys.map(sym => {
          const group = groupedBySymbol[sym];
          const isCollapsed = collapsedGroups[sym];
          const impact = dominantImpact(group.items);
          const panelId = `news-group-${sym.replace(/[^\w-]/g, '_')}`;
          return (
            <Card key={sym} padding="none" className="overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 sm:px-5">
                <Link
                  to={`/stock/${encodeURIComponent(sym)}`}
                  className="inline-flex h-7 flex-shrink-0 items-center rounded-md border border-line bg-surface-2 px-2 text-xs font-semibold text-gold-300 transition-colors hover:border-gold-500/40 hover:text-gold-200"
                >
                  {sym}
                </Link>
                <button
                  type="button"
                  onClick={() => toggleGroup(sym)}
                  aria-expanded={!isCollapsed}
                  aria-controls={panelId}
                  className="flex min-w-0 flex-1 items-center gap-3 rounded-md py-1 text-left"
                >
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">{group.company}</span>
                  <span className="hidden sm:inline-flex"><Badge tone={impactOf(impact).tone} dot>{DOMINANT_LABELS[impact]}</Badge></span>
                  <span className="num flex-shrink-0 text-xs text-fg-subtle">{plural(group.items.length, 'article', 'articles')}</span>
                  <ChevronDown size={16} aria-hidden="true" className={cx('flex-shrink-0 text-fg-subtle transition-transform duration-200', isCollapsed && '-rotate-90')} />
                </button>
              </div>
              {!isCollapsed && (
                <div id={panelId} className="divide-y divide-line border-t border-line px-4 sm:px-5">
                  {group.items.map((item, i) => (
                    <NewsItem key={`${item.url || item.headline}-${i}`} item={item} showSymbol={false} flush={false} />
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Intelligence"
        title="Actualités"
        description={allItems.length ? `${plural(allItems.length, 'article', 'articles')} sur ${plural(symbols.length, 'valeur', 'valeurs')}, portefeuille et watchlist.` : 'Les actualités de vos positions et de votre watchlist.'}
        actions={saxoConnected && (
          <Button variant="secondary" leadingIcon={RefreshCw} loading={busy} onClick={handleRefresh}>
            Tout actualiser
          </Button>
        )}
      />
      <BrokerGate>
        <div className="space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm('')}
              placeholder="Rechercher une actualité…"
              aria-label="Rechercher une actualité"
              className="w-full sm:max-w-sm"
            />
            <SegmentedControl ariaLabel="Filtrer par impact" options={IMPACT_FILTERS} value={impactFilter} onChange={setImpactFilter} />
          </div>

          {symbols.length > 0 && (
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer par valeur">
              <Chip active={!searchTerm} onClick={() => setSearchTerm('')}>Toutes</Chip>
              {symbols.map(sym => (
                <Chip key={sym} active={searchTerm === sym} onClick={() => setSearchTerm(searchTerm === sym ? '' : sym)}>{sym}</Chip>
              ))}
            </div>
          )}

          {isGenerating && (
            <Banner tone="info" role="status">
              Analyse des actualités de vos valeurs… {allItems.length > 0 ? `${plural(allItems.length, 'article', 'articles')} sur ${plural(symbols.length, 'valeur', 'valeurs')} pour l'instant.` : 'Démarrage de la recherche.'}
            </Banner>
          )}

          {content}

          {latestNews && !isGenerating && latestNews.generated_at && (
            <p className="text-center text-xs text-fg-subtle">Mis à jour le {formatDateTime(latestNews.generated_at)}</p>
          )}
        </div>
      </BrokerGate>
    </div>
  );
};

export default LatestNews;
